import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/lib/hooks/use-toast';
import { adminService } from '../services/admin-service';
import type { ResumenDelClon } from '../types/admin.types';

/**
 * Copiar una empresa entera a una nueva.
 *
 * ⚖️ **El ensayo es el primer paso, no un extra.** El botón de clonar de verdad no
 * existe hasta que hay un resumen en pantalla: copiar una empresa es una
 * operación de minutos que crea decenas de miles de filas, y la propia
 * especificación pone «volver a medir antes de clonar» como su riesgo número uno.
 * Ver los números antes ES esa medición.
 */
export function ClonarEmpresaDialog({
  empresaId,
  nombreOrigen,
  abierto,
  onCerrar,
  onClonada,
}: {
  empresaId: string;
  nombreOrigen: string;
  abierto: boolean;
  onCerrar: () => void;
  onClonada?: () => void;
}) {
  const { toast } = useToast();
  const [nombre, setNombre] = useState('');
  const [direccion, setDireccion] = useState('');
  const [incluirUsuarios, setIncluirUsuarios] = useState(true);
  const [resumen, setResumen] = useState<ResumenDelClon | null>(null);
  const [trabajando, setTrabajando] = useState<'ensayo' | 'clon' | null>(null);
  const [verDetalle, setVerDetalle] = useState(false);

  /**
   * La dirección se propone desde el nombre y se puede editar. Se propone y no se
   * impone porque el nombre puede llevar mayúsculas, tildes y espacios, y la
   * dirección no.
   */
  const cambiarNombre = (v: string) => {
    setNombre(v);
    setResumen(null);
    const propuesta = v
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setDireccion(propuesta);
  };

  const cerrarYLimpiar = () => {
    setNombre('');
    setDireccion('');
    setResumen(null);
    setVerDetalle(false);
    onCerrar();
  };

  const avisarDelError = (err: unknown) => {
    const detalle = (err as { response?: { data?: { message?: string; error?: string } } })
      ?.response?.data;
    toast({
      title: 'No se pudo copiar la empresa',
      description: detalle?.message ?? detalle?.error ?? 'Intentá de nuevo.',
      variant: 'destructive',
    });
  };

  const verQueCopiaria = async () => {
    setTrabajando('ensayo');
    try {
      setResumen(
        await adminService.clonarEmpresa(empresaId, {
          name: nombre,
          slug: direccion,
          incluirUsuarios,
          ensayo: true,
        })
      );
    } catch (err) {
      setResumen(null);
      avisarDelError(err);
    } finally {
      setTrabajando(null);
    }
  };

  const clonar = async () => {
    setTrabajando('clon');
    try {
      const hecho = await adminService.clonarEmpresa(empresaId, {
        name: nombre,
        slug: direccion,
        incluirUsuarios,
      });
      toast({
        title: `${nombre} está lista`,
        description: `Se copiaron ${hecho.totalDeFilas.toLocaleString('es-BO')} filas${
          hecho.membresiasCreadas > 0 ? ` y entran ${hecho.membresiasCreadas} personas` : ''
        }.`,
      });
      onClonada?.();
      cerrarYLimpiar();
    } catch (err) {
      avisarDelError(err);
    } finally {
      setTrabajando(null);
    }
  };

  const listoParaEnsayar = nombre.trim().length >= 2 && direccion.length >= 2;
  const tablasConFilas = resumen
    ? Object.entries(resumen.filasPorTabla)
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1])
    : [];

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && cerrarYLimpiar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Copiar {nombreOrigen}</DialogTitle>
          <DialogDescription>
            La empresa nueva nace con todo lo que tiene {nombreOrigen}: sus cotizaciones, sus
            clientes y su catálogo. Las integraciones, las fuentes de datos externas y las tiendas
            que se leen se copian apagadas, para no volver a leer lo mismo.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="clon-nombre">Nombre de la empresa nueva</Label>
            <Input
              id="clon-nombre"
              value={nombre}
              onChange={(e) => cambiarNombre(e.target.value)}
              placeholder="DEMO"
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="clon-direccion">Dirección</Label>
            <Input
              id="clon-direccion"
              value={direccion}
              onChange={(e) => {
                setDireccion(e.target.value);
                setResumen(null);
              }}
              placeholder="demo"
              autoComplete="off"
            />
            <p className="text-muted-foreground text-xs">
              Va adelante en la dirección web:{' '}
              <span className="font-mono">/{direccion || '…'}</span>
            </p>
          </div>

          <label className="flex items-start gap-2.5 text-sm">
            <Checkbox
              checked={incluirUsuarios}
              onCheckedChange={(v) => {
                setIncluirUsuarios(v === true);
                setResumen(null);
              }}
              className="mt-0.5"
            />
            <span>
              Que las mismas personas puedan entrar
              <span className="text-muted-foreground block text-xs">
                Entran con su misma cuenta y su misma contraseña. Sin esto, la empresa nueva nace
                sin nadie que pueda abrirla.
              </span>
            </span>
          </label>

          {resumen && (
            <div className="bg-muted/50 rounded-md border p-3">
              <p className="text-sm font-medium">
                Se van a copiar {resumen.totalDeFilas.toLocaleString('es-BO')} filas
              </p>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {tablasConFilas.length} tablas con datos
                {incluirUsuarios ? ' · las personas entran con su misma cuenta' : ''}
              </p>
              <button
                type="button"
                onClick={() => setVerDetalle((v) => !v)}
                className="text-primary mt-2 text-xs underline"
              >
                {verDetalle ? 'Ocultar el detalle' : 'Ver el detalle'}
              </button>
              {verDetalle && (
                <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto font-mono text-xs">
                  {tablasConFilas.map(([tabla, n]) => (
                    <li key={tabla} className="flex justify-between gap-4">
                      <span className="truncate">{tabla}</span>
                      <span className="tabular-nums">{n.toLocaleString('es-BO')}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={cerrarYLimpiar} disabled={trabajando !== null}>
            Cancelar
          </Button>
          {!resumen ? (
            <Button onClick={verQueCopiaria} disabled={!listoParaEnsayar || trabajando !== null}>
              {trabajando === 'ensayo' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ver qué copiaría
            </Button>
          ) : (
            <Button onClick={clonar} disabled={trabajando !== null}>
              {trabajando === 'clon' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Clonar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ClonarEmpresaDialog;
